import React, { useMemo, useState } from "react";
import {
  Box,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { NAVY } from "./ReportParts";
import {
  CARD,
  GRID,
  INK,
  InfoTip,
  SigLabel,
  MUTED,
  GroupLabel,
  isClear,
  fmtLift,
  fmtValue,
  metricDefinitions,
} from "./experimentParts";

const SIGNAL_LABELS = {
  vote: "Vote",
  like: "Like",
  comment: "Comment",
  commentsOpened: "Opened comments",
  profileView: "Profile view",
  replay: "Replay",
  longWatch: "Long watch",
  quickSkip: "Quick skip",
  leftOnCard: "Left the app",
};

const SETTING_LABELS = {
  allocation: ["Split", (v) => `${Math.round(v * 100)}%`],
  learningRate: ["Learning", (v) => String(v)],
  maxMultiplier: ["Cap", (v) => `${v}×`],
  halfLifeDays: ["Fade", (v) => `${v}d`],
  quickSkipMs: ["Quick skip", (v) => `${v / 1000}s`],
  longWatchMs: ["Long watch", (v) => `${v / 1000}s`],
};

/** One config change as short lines, e.g. "Learning 0.05 → 0.1". */
export const describeChange = ({ patch = {}, before = {} }) => {
  const lines = [];
  for (const [key, value] of Object.entries(patch)) {
    if (key === "enabled") lines.push(value ? "Started" : "Stopped");
    else if (key === "signalWeights") {
      for (const [signal, weight] of Object.entries(value || {})) {
        lines.push(
          `${SIGNAL_LABELS[signal] || signal} weight ${
            before.signalWeights?.[signal] ?? "–"
          } → ${weight}`
        );
      }
    } else if (SETTING_LABELS[key]) {
      const [label, fmt] = SETTING_LABELS[key];
      lines.push(
        `${label} ${before[key] != null ? fmt(before[key]) : "–"} → ${fmt(value)}`
      );
    }
  }
  return lines;
};

const periodLabel = (key, interval) =>
  interval === "week"
    ? `Wk ${dayjs(key).format("MMM D")}`
    : dayjs(key).format("MMM D");

const periodOfDate = (date, interval) => {
  const day = dayjs(date).format("YYYY-MM-DD");
  if (interval !== "week") return day;
  const d = dayjs(day);
  return d.subtract((d.day() + 6) % 7, "day").format("YYYY-MM-DD");
};

/**
 * Change vs control per bucket, one shared vertical scale: a point with
 * its 95% interval, colored by verdict. `connect` draws the over-time
 * line and interval band instead of whiskers.
 */
const LiftColumns = ({
  title,
  info,
  xTitle,
  points,
  metric,
  domain,
  connect,
  markers,
  groups,
}) => {
  const W = 320;
  const H = 226;
  const pad = { top: 26, right: 10, bottom: 44, left: 40 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;
  const band = plotW / Math.max(1, points.length);
  const cx = (i) => pad.left + band * (i + 0.5);
  const clamp = (v) => Math.max(-domain, Math.min(domain, v));
  const y = (v) => pad.top + ((domain - clamp(v)) / (2 * domain)) * plotH;
  const [hover, setHover] = useState(null);
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));
  // A group's comparison at a point (older single-group results have no vs).
  const cmpAt = (p, g) => {
    const m = p.metrics[metric.key];
    return m?.vs?.[g.key] ?? (g.key === "personalized" ? m : null);
  };
  // Groups sit side by side within a column so their whiskers don't overlap.
  const offset = (gi) => (gi - (groups.length - 1) / 2) * 7;
  const series = groups.map((g, gi) => ({
    g,
    gi,
    pts: points
      .map((p, i) => ({ i, c: cmpAt(p, g) }))
      .filter((p) => p.c?.lift != null),
  }));

  return (
    <Paper
      elevation={0}
      sx={{ ...CARD, p: "12px 14px 6px", position: "relative" }}
    >
      <Typography
        sx={{ fontSize: "13px", fontWeight: 600, color: MUTED, mb: 0.5 }}
      >
        {title}
        <InfoTip text={info} />
      </Typography>
      {points.length === 0 ? (
        <Box
          sx={{
            height: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: MUTED,
            fontSize: "12.5px",
          }}
        >
          No sessions yet
        </Box>
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={{ display: "block" }}
          role="img"
          aria-label={`${title}: ${metric.label} vs control by ${xTitle}`}
        >
          {[-domain, -domain / 2, 0, domain / 2, domain].map((t) => (
            <g key={t}>
              <line
                x1={pad.left}
                x2={W - pad.right}
                y1={y(t)}
                y2={y(t)}
                stroke={t === 0 ? "#c4c9d1" : GRID}
                strokeWidth={t === 0 ? 1.5 : 1}
              />
              <text
                x={pad.left - 6}
                y={y(t) + 3.5}
                textAnchor="end"
                fontSize="10"
                fill={MUTED}
              >
                {t === 0 ? "0" : `${t > 0 ? "+" : "−"}${Math.abs(t)}%`}
              </text>
            </g>
          ))}

          {(markers || []).map((mk) => (
            <line
              key={`mk-${mk.index}`}
              x1={cx(mk.index)}
              x2={cx(mk.index)}
              y1={pad.top}
              y2={pad.top + plotH}
              stroke={NAVY}
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.55"
            />
          ))}

          {connect &&
            series.length === 1 &&
            series[0].pts.length > 1 && (
              <path
                d={`M${series[0].pts
                  .map((p) => `${cx(p.i)},${y(p.c.high)}`)
                  .join(" L")} L${[...series[0].pts]
                  .reverse()
                  .map((p) => `${cx(p.i)},${y(p.c.low)}`)
                  .join(" L")} Z`}
                fill="#e9edf3"
              />
            )}
          {connect &&
            series.map(({ g, pts }) =>
              pts.length > 1 ? (
                <polyline
                  key={`line-${g.key}`}
                  points={pts.map((p) => `${cx(p.i)},${y(p.c.lift)}`).join(" ")}
                  fill="none"
                  stroke={g.color}
                  strokeWidth="2"
                  strokeLinejoin="round"
                  opacity="0.8"
                />
              ) : null
            )}

          {series.map(({ g, gi, pts }) =>
            pts.map((p) => {
              const x = cx(p.i) + (connect ? 0 : offset(gi));
              return (
                <g key={`${g.key}-${p.i}`}>
                  {!connect && (
                    <line
                      x1={x}
                      x2={x}
                      y1={y(p.c.high)}
                      y2={y(p.c.low)}
                      stroke={g.color}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  )}
                  <circle
                    cx={x}
                    cy={y(p.c.lift)}
                    r="4"
                    fill={isClear(p.c.verdict) ? g.color : "#fff"}
                    stroke={g.color}
                    strokeWidth="2"
                  />
                </g>
              );
            })
          )}

          {/* Tags last so no point or whisker covers them: past the
              interval's end (or the point, on lines), groups alternating
              above / below. */}
          {series.map(({ g, gi, pts }) =>
            pts.map((p) => (
              <SigLabel
                key={`tag-${g.key}-${p.i}`}
                x={cx(p.i) + (connect ? 0 : offset(gi))}
                y={
                  connect
                    ? y(p.c.lift) + (gi % 2 === 0 ? -3 : 3)
                    : y(gi % 2 === 0 ? p.c.high : p.c.low)
                }
                cmp={p.c}
                side={gi % 2 === 0 ? "above" : "below"}
                fontSize={7.5}
              />
            ))
          )}

          <text x={pad.left - 34} y={11} fontSize="9.5" fill={INK} fontWeight="600">
            {`${metric.label}: ${groups.length > 1 ? "each group" : "personalized"} vs control`}
          </text>
          <text
            x={pad.left + plotW / 2}
            y={H - 4}
            textAnchor="middle"
            fontSize="10"
            fill={INK}
            fontWeight="600"
          >
            {xTitle}
          </text>

          {points.map((p, i) =>
            i % labelEvery === 0 ? (
              <text
                key={`x-${p.key}`}
                x={cx(i)}
                y={H - 24}
                textAnchor="middle"
                fontSize="10"
                fill={MUTED}
              >
                {p.label}
              </text>
            ) : null
          )}

          {points.map((p, i) => (
            <rect
              key={`hit-${p.key}`}
              x={pad.left + band * i}
              y={pad.top}
              width={band}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover({ p, i })}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
      )}
      {hover && (
        <Box
          sx={{
            position: "absolute",
            top: 46,
            left: `${(cx(hover.i) / W) * 100}%`,
            transform:
              cx(hover.i) > W / 2
                ? "translateX(calc(-100% - 14px))"
                : "translateX(14px)",
            background: "#fff",
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 14px rgba(20,44,92,0.12)",
            borderRadius: "8px",
            p: "8px 10px",
            fontSize: "12px",
            lineHeight: 1.55,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 3,
          }}
        >
          <Box sx={{ fontWeight: 700, color: NAVY }}>{hover.p.label}</Box>
          {groups.map((g) => {
            const c = cmpAt(hover.p, g);
            const m = hover.p.metrics[metric.key];
            return (
              <Box key={g.key} sx={{ color: INK }}>
                {groups.length > 1 && <GroupLabel variant={g} />}
                {groups.length > 1 && " · "}
                {c?.lift != null
                  ? `${fmtLift(c.lift)} (95% ${fmtLift(c.low)} to ${fmtLift(
                      c.high
                    )}) · ${fmtValue(m?.control, metric.unit)} → ${fmtValue(
                      m?.means?.[g.key] ?? m?.personalized,
                      metric.unit
                    )}`
                  : "Not enough data"}
              </Box>
            );
          })}
          <Box sx={{ color: MUTED }}>
            Users{" "}
            {["control", ...groups.map((g) => g.key)]
              .map((k) => (hover.p.users?.[k] || 0).toLocaleString())
              .join(" / ")}{" "}
            · sessions{" "}
            {["control", ...groups.map((g) => g.key)]
              .map((k) => (hover.p.sessions?.[k] || 0).toLocaleString())
              .join(" / ")}
          </Box>
          {(markers || [])
            .filter((mk) => mk.index === hover.i)
            .flatMap((mk) => mk.lines)
            .map((line, k) => (
              <Box key={k} sx={{ color: NAVY }}>
                {line}
              </Box>
            ))}
        </Box>
      )}
    </Paper>
  );
};

/**
 * Trends for one metric: by how much the feed had learned, by session
 * number in the run, and over time, each personalized group vs control.
 * Filled point = clear difference; hollow = could be chance.
 */
export default function TrendsPanel({ data, groups }) {
  const metrics = data.metrics || [];
  const [metricKey, setMetricKey] = useState("minutesPerSession");
  const metric = metrics.find((m) => m.key === metricKey) || metrics[0];

  const series = useMemo(() => {
    const byLearning = (data.byLearning || []).map((b) => ({
      ...b,
      label: b.key.replace("-", "–"),
    }));
    const bySession = (data.bySession || []).map((b) => ({
      ...b,
      label: b.key === "1" ? "1st" : b.key.replace("-", "–"),
    }));
    const overTime = (data.overTime || []).map((b) => ({
      ...b,
      label: periodLabel(b.key, data.interval),
    }));
    return { byLearning, bySession, overTime };
  }, [data]);

  const markers = useMemo(() => {
    const byIndex = new Map();
    for (const change of data.changes || []) {
      const index = series.overTime.findIndex(
        (p) => p.key === periodOfDate(change.at, data.interval)
      );
      if (index < 0) continue;
      const lines = describeChange(change).map(
        (line) => `${dayjs(change.at).format("MMM D, h:mm A")} · ${line}`
      );
      byIndex.set(index, [...(byIndex.get(index) || []), ...lines]);
    }
    return [...byIndex].map(([index, lines]) => ({ index, lines }));
  }, [data, series.overTime]);

  if (!metric) return null;

  // One scale for all three charts: the widest interval, at least ±10%,
  // in tens so the half-way gridlines are whole numbers.
  const domain = Math.max(
    10,
    ...[...series.byLearning, ...series.bySession, ...series.overTime]
      .map((p) => p.metrics[metric.key])
      .flatMap((m) => Object.values(m?.vs || { personalized: m }))
      .filter((c) => c?.lift != null)
      .flatMap((c) => [c.low, c.high])
      .map((v) => Math.min(100, Math.ceil(Math.abs(v) / 10) * 10))
  );

  return (
    <Paper elevation={0} sx={{ ...CARD, p: "14px 18px", mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
          Trends
          <InfoTip text="% difference vs control. Filled = clear difference, hollow = could be chance." />
        </Typography>
        <Select
          size="small"
          value={metric.key}
          onChange={(e) => setMetricKey(e.target.value)}
          sx={{ fontSize: "13px", height: 30, minWidth: 220 }}
        >
          <ListSubheader>Metrics</ListSubheader>
          {metrics
            .filter((m) => m.kind === "metric")
            .map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          <ListSubheader>Countermetrics</ListSubheader>
          {metrics
            .filter((m) => m.kind === "counter")
            .map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
        </Select>
        <InfoTip text={metricDefinitions(data.config)[metric.key]} />
        <Box sx={{ display: "flex", gap: 2, ml: "auto", flexWrap: "wrap", fontSize: "13px" }}>
          {groups.length > 1 && groups.map((g) => <GroupLabel key={g.key} variant={g} />)}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        <LiftColumns
          title="As the feed learns"
          info="Sessions grouped by how many interactions the feed had learned from. Control learns too, unseen. Rising = learning helps."
          xTitle="Interactions learned before the session"
          points={series.byLearning}
          groups={groups}
          metric={metric}
          domain={domain}
        />
        <LiftColumns
          title="As users return"
          info="Sessions grouped by the user's session count this run."
          xTitle="Session number this run"
          points={series.bySession}
          groups={groups}
          metric={metric}
          domain={domain}
        />
        <LiftColumns
          title="Over time"
          info="Band = 95% range. Dashed line = settings change."
          xTitle={data.interval === "week" ? "Week starting" : "Day"}
          points={series.overTime}
          groups={groups}
          metric={metric}
          domain={domain}
          connect
          markers={markers}
        />
      </Box>
    </Paper>
  );
}
